import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import KebabMenuIcon from "../../assets/svg/kebabMenu.svg";
import LutronDesignerClientProxy from "../../backend/clients/LutronDesignerClientProxy";
import useOutsideClick from "../../hooks/useOutsideClick";
import useVersionData from "../../hooks/useVersionData";
import {
  Artifact,
  ArtifactProjectType,
  LutronDesignerMessageType,
} from "../../models";
import Menu, { MenuContainer, MenuItem } from "../../ui/Menu/Menu";
import ErrorModal from "../../ui/Modal/ErrorModal";
import Modal from "../../ui/Modal/Modal";
import VersionHistory from "../VersionHistory/VersionHistory";
import VersionHistoryLoader from "../VersionHistory/VersionHistoryLoader";
import { useServiceWorkerContext } from "../ServiceWorkerContext/ServiceWorkerContext";
import { useToolContext } from "../ToolContext/ToolContext";

const PopupMenuContainer = styled.div`
  position: absolute;
  top: 40px;
  left: -18px;
  z-index: 50;
  border-radius: 12px;
  background: white;
  box-shadow: 0px 13.005587577819824px 39.016761779785156px 0px
    rgba(0, 0, 0, 0.16);

  ${MenuContainer} {
    padding: 12px;
    background: white;
    border-radius: 16px;
  }
`;

const PopupMenuPointer = styled.div`
  position: absolute;
  transform: rotate(45deg);
  top: -8px;
  left: 32px;
  height: 16px;
  width: 16px;
  z-index: 50;
  box-shadow: 0px 13.005587577819824px 39.016761779785156px 0px
    rgba(0, 0, 0, 0.16);
  background: white;
  clip-path: polygon(0 0, 0% 100%, 100% 0);
`;

export const TableMoreIconContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

const TableMoreIcon = styled.img`
  margin-left: 10px;
  height: 75%;
  padding: 12.5% 0px;
  cursor: pointer;
`;

type Props = {
  artifact: Artifact;
};

const ArtifactKebabMenu = ({ artifact }: Props): JSX.Element => {
  const { t } = useTranslation();
  const sw = useServiceWorkerContext();
  const context = useToolContext();
  const { getVersions, data, error, loading } = useVersionData(artifact);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const ref = useOutsideClick<HTMLDivElement>(setOpen);

  const migrateToAthena = useCallback(() => {
    const migrateArtifact = {
      artifactID: artifact.id,
      artifactHref: artifact.href,
    };

    LutronDesignerClientProxy.migrateToAthenaRequest(
      sw.waitForInstalled,
      migrateArtifact,
    );
  }, [artifact, sw]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const menuItems = useMemo(() => {
    const items = new Map<string, MenuItem[]>();

    const menuList: MenuItem[] = [];

    if (
      context.capabilities?.includes(
        LutronDesignerMessageType.OpenVersionRequest,
      )
    ) {
      menuList.push({
        label: t("table.kebab_menu.version_history"),
        key: "version-history",
        disabled: context.isOffline,
        onClick: () => {
          setIsModalOpen(true);
          setOpen(false);
          getVersions();
        },
      });
    }

    if (
      artifact.artifactProjectType === ArtifactProjectType.LDPlus &&
      context.capabilities?.includes(
        LutronDesignerMessageType.MigrateToAthenaRequest,
      )
    ) {
      menuList.push({
        label: t("table.kebab_menu.migrate_to_athena"),
        key: "migrate-athena",
        onClick: migrateToAthena,
      });
    }

    items.set(t("table.kebab_menu.heading"), menuList);

    return items;
  }, [
    artifact.artifactProjectType,
    context.capabilities,
    context.isOffline,
    getVersions,
    migrateToAthena,
    t,
  ]);

  return (
    <TableMoreIconContainer data-open={open} ref={ref}>
      <TableMoreIcon
        data-testid={`kebab[${artifact.id}]`}
        src={KebabMenuIcon}
        onClick={() => setOpen(!open)}
      />
      {open && (
        <PopupMenuContainer>
          <PopupMenuPointer />
          <Menu menuItems={menuItems} />
        </PopupMenuContainer>
      )}
      {isModalOpen && (
        <Modal>
          {!data && !error && <VersionHistoryLoader artifact={artifact} />}
          {data ? (
            <VersionHistory
              artifact={artifact}
              version={data}
              onClose={closeModal}
              loading={loading}
            />
          ) : (
            error && (
              <ErrorModal
                header={error.message.split("|")[0]}
                message={error.message.split("|")[1]}
                onClose={closeModal}
              />
            )
          )}
        </Modal>
      )}
    </TableMoreIconContainer>
  );
};

export default ArtifactKebabMenu;
